import { useEffect, useState, memo } from "react";
import { createPortal } from "react-dom";

// Function to determine if a color is light or dark
const isLightColor = (color) => {
  if (!color || typeof color !== 'string') return true;
  
  let r, g, b;
  
  if (color.startsWith('#')) {
    // Handle hex color
    const hex = color.replace('#', '');
    if (hex.length === 3) {
      // Short hex format (#RGB)
      r = parseInt(hex.charAt(0) + hex.charAt(0), 16);
      g = parseInt(hex.charAt(1) + hex.charAt(1), 16);
      b = parseInt(hex.charAt(2) + hex.charAt(2), 16);
    } else if (hex.length === 6) {
      // Long hex format (#RRGGBB)
      r = parseInt(hex.substring(0, 2), 16);
      g = parseInt(hex.substring(2, 4), 16);
      b = parseInt(hex.substring(4, 6), 16);
    } else {
      return true; // Default to light if invalid hex
    }
  } else if (color.startsWith('rgb')) {
    // Handle rgb/rgba color
    const match = color.match(/\d+/g);
    if (match && match.length >= 3) {
      r = parseInt(match[0]);
      g = parseInt(match[1]);
      b = parseInt(match[2]);
    } else {
      return true; // Default to light if invalid rgb
    }
  } else {
    return true; // Default to light for unknown formats
  }
  
  // Calculate luminance using the standard formula
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  
  return luminance > 0.5;
};

// Tracks whether the intro logo animation has already played during this page
// load. The full-screen loader can mount several times in sequence (app init →
// route Suspense → SurveyViewer data fetch); without this guard the logo would
// replay its fade-in on every mount, which looks flaky ("logo appears twice").
// We let it play once and render the final state on later mounts.
let hasLogoIntroPlayed = false;

// PollBolt Logo Component  
const PollBoltLogo = memo(({ isLight }) => {
  const fillColor = isLight ? "black" : "white";
  const textColor = isLight ? "black" : "white";
  const [showAnimation, setShowAnimation] = useState(!hasLogoIntroPlayed);
  
  useEffect(() => {
    if (!showAnimation) return undefined;
    // Mark immediately so any sibling/subsequent loader mount skips the intro.
    hasLogoIntroPlayed = true;
    const timer = setTimeout(() => {
      setShowAnimation(false);
    }, 800);
    
    return () => clearTimeout(timer);
  }, [showAnimation]);
  
  return (
    <div 
      className={`flex flex-col items-center ${showAnimation ? 'logo-fade-in' : ''}`}
      style={!showAnimation ? { opacity: 1, transform: 'translateY(0)' } : {}}
    >
      <p className="text-xs mb-1 opacity-60" style={{ color: textColor }}>Powered by</p>
      <svg 
        width="102" 
        height="24" 
        viewBox="0 0 136 32" 
        fill="none" 
        xmlns="http://www.w3.org/2000/svg"
      >
      <path d="M44.6594 6.69022C46.82 6.69022 48.5806 7.36831 49.941 8.72447C51.3013 10.0709 51.9815 11.7344 51.9815 13.715C51.9815 15.7053 51.3013 17.359 49.941 18.6762C48.5906 19.9933 46.83 20.6519 44.6594 20.6519H38.7927V29.1693H35.2517V6.69022H44.6594ZM44.1943 17.6664C45.5546 17.6664 46.6149 17.3152 47.3752 16.6127C48.1354 15.9102 48.5156 14.9394 48.5156 13.7003C48.5156 12.4905 48.1304 11.5197 47.3602 10.7879C46.59 10.0465 45.5347 9.67576 44.1943 9.67576H38.7927V17.6664H44.1943Z" fill={fillColor}/>
      <path d="M64.1141 28.4084C62.8137 29.1108 61.3783 29.462 59.8078 29.462C58.2374 29.462 56.8019 29.1108 55.5016 28.4084C54.2012 27.7059 53.1759 26.74 52.4257 25.5106C51.6854 24.2813 51.3154 22.9154 51.3154 21.4129C51.3154 19.9201 51.6854 18.5591 52.4257 17.3298C53.1759 16.1004 54.2012 15.1346 55.5016 14.4321C56.8019 13.7296 58.2374 13.3783 59.8078 13.3783C61.3783 13.3783 62.8137 13.7296 64.1141 14.4321C65.4144 15.1346 66.4347 16.1004 67.175 17.3298C67.9252 18.5591 68.3003 19.9201 68.3003 21.4129C68.3003 22.9154 67.9252 24.2813 67.175 25.5106C66.4347 26.74 65.4144 27.7059 64.1141 28.4084ZM56.0718 25.0716C57.092 26.0473 58.3324 26.5351 59.7928 26.5351C61.2532 26.5351 62.4936 26.0424 63.5139 25.057C64.5342 24.0716 65.0443 22.8569 65.0443 21.4129C65.0443 19.9787 64.5342 18.7689 63.5139 17.7835C62.4936 16.798 61.2532 16.3053 59.7928 16.3053C58.3324 16.3053 57.092 16.798 56.0718 17.7835C55.0614 18.7591 54.5563 19.9689 54.5563 21.4129C54.5563 22.8666 55.0614 24.0861 56.0718 25.0716Z" fill={fillColor}/>
      <path d="M70.4249 29.1693V6.69022H73.7709V29.1693H70.4249Z" fill={fillColor}/>
      <path d="M77.2608 29.1693V6.69022H80.6069V29.1693H77.2608Z" fill={fillColor}/>
      <path d="M96.6405 17.5346C98.0005 17.8957 99.0659 18.5493 99.8367 19.4958C100.607 20.4323 100.992 21.6324 100.992 23.0959C100.992 24.9106 100.367 26.379 99.1158 27.501C97.8763 28.6133 96.2358 29.1693 94.1944 29.1693H84.742V6.69022H92.3643C94.4344 6.69022 96.1106 7.24635 97.3906 8.3586C98.6716 9.47087 99.3116 10.9392 99.3116 12.7637C99.3116 14.9784 98.4212 16.5687 96.6405 17.5346ZM95.9506 13.0271C95.9506 12.0222 95.6504 11.2221 95.0499 10.627C94.4598 10.0318 93.5347 9.7343 92.2742 9.7343H88.2081V16.3785H92.2742C93.3646 16.3785 94.2499 16.0614 94.9304 15.4273C95.6099 14.7931 95.9506 13.993 95.9506 13.0271ZM93.7297 26.0814C94.9398 26.0814 95.8706 25.7643 96.5201 25.1302C97.1808 24.4862 97.5111 23.6765 97.5111 22.7007C97.5111 21.7446 97.161 20.9397 96.4608 20.286C95.7699 19.6323 94.8598 19.3055 93.7297 19.3055H88.2081V26.0814H93.7297Z" fill={fillColor}/>
      <path d="M114.864 28.4084C113.565 29.1108 112.129 29.462 110.559 29.462C108.988 29.462 107.552 29.1108 106.253 28.4084C104.952 27.7059 103.927 26.74 103.176 25.5106C102.436 24.2813 102.066 22.9154 102.066 21.4129C102.066 19.9201 102.436 18.5591 103.176 17.3298C103.927 16.1004 104.952 15.1346 106.253 14.4321C107.552 13.7296 108.988 13.3783 110.559 13.3783C112.129 13.3783 113.565 13.7296 114.864 14.4321C116.165 15.1346 117.185 16.1004 117.926 17.3298C118.676 18.5591 119.051 19.9201 119.051 21.4129C119.051 22.9154 118.676 24.2813 117.926 25.5106C117.185 26.74 116.165 27.7059 114.864 28.4084ZM106.822 25.0716C107.842 26.0473 109.083 26.5351 110.544 26.5351C112.004 26.5351 113.245 26.0424 114.265 25.057C115.285 24.0716 115.795 22.8569 115.795 21.4129C115.795 19.9787 115.285 18.7689 114.265 17.7835C113.245 16.798 112.004 16.3053 110.544 16.3053C109.083 16.3053 107.842 16.798 106.822 17.7835C105.812 18.7591 105.307 19.9689 105.307 21.4129C105.307 22.8666 105.812 24.0861 106.822 25.0716Z" fill={fillColor}/>
      <path d="M121.176 29.1693V6.69022H124.522V29.1693H121.176Z" fill={fillColor}/>
      <path d="M135.874 16.1736H131.958V24.2521C131.958 24.9838 132.153 25.5497 132.542 25.9497C132.933 26.3498 133.458 26.5498 134.118 26.5498H135.844V29.1693H133.443C131.963 29.1693 130.787 28.7742 129.918 27.9839C129.047 27.1936 128.612 26.0765 128.612 24.6326V16.1736H126.016V13.671H128.612V9.38305H131.958V13.671H135.874V16.1736Z" fill={fillColor}/>
      <path d="M17.0987 15.9043H26.2578C26.2855 15.9683 26.3113 16.0344 26.3391 16.0985C19.6269 21.3469 12.9148 26.5952 6.20455 31.8455C6.13515 31.8105 6.06574 31.7755 5.99634 31.7406C6.94021 26.4864 7.88606 21.2303 8.85571 15.8324H0.073368C0.049573 15.7586 0.023795 15.6848 0 15.611C6.58328 10.4617 13.1665 5.31631 19.9641 0C18.9845 5.43092 18.0585 10.5782 17.0987 15.9043Z" fill={fillColor}/>
      </svg>
    </div>
  );
});

export function AppLoader({
  size = 6,
  color = "black",
  coverage = "self",
  className,
  backgroundColor = "#FFFFFF",
}) {
  const [portalContainer, setPortalContainer] = useState(null);
  const [effectiveBackgroundColor, setEffectiveBackgroundColor] = useState(() => {
    // Try to get from localStorage immediately on mount for survey pages
    if (coverage === "full" && typeof window !== 'undefined') {
      const path = window.location.pathname;
      const surveyMatch = path.match(/\/s\/([^\/]+)/);
      if (surveyMatch) {
        const surveyId = surveyMatch[1];
        const storedColor = localStorage.getItem(`survey_${surveyId}_background_color`);
        if (storedColor && storedColor !== "#FFFFFF") {
          return storedColor;
        }
      }
    }
    return backgroundColor;
  });
  const [hasReceivedColor, setHasReceivedColor] = useState(() => {
    // Check if we already have a color on mount
    if (coverage === "full" && typeof window !== 'undefined') {
      const path = window.location.pathname;
      const surveyMatch = path.match(/\/s\/([^\/]+)/);
      if (surveyMatch) {
        const surveyId = surveyMatch[1];
        const storedColor = localStorage.getItem(`survey_${surveyId}_background_color`);
        return storedColor && storedColor !== "#FFFFFF";
      }
    }
    return backgroundColor !== "#FFFFFF";
  });

  useEffect(() => {
    if (coverage === "full") {
      const container = document.getElementById("loader-portal");
      if (container) {
        setPortalContainer(container);
      }
    }
  }, [coverage]);

  // Get effective background color on mount and when backgroundColor changes
  useEffect(() => {
    const updateBackgroundColor = () => {
      if (coverage === "full") {
        // Check for survey page
        const path = window.location.pathname;
        const surveyMatch = path.match(/\/s\/([^\/]+)/);
        
        if (surveyMatch) {
          const surveyId = surveyMatch[1];
          // First try localStorage
          const storedColor = localStorage.getItem(`survey_${surveyId}_background_color`);
          if (storedColor && storedColor !== "#FFFFFF") {
            setEffectiveBackgroundColor(storedColor);
            setHasReceivedColor(true);
            return;
          }
          
          // Then check CSS variable only for survey pages
          const cssVariable = getComputedStyle(document.documentElement).getPropertyValue('--survey-bg-color');
          if (cssVariable && cssVariable.trim()) {
            setEffectiveBackgroundColor(cssVariable.trim());
            setHasReceivedColor(true);
            return;
          }
        }
        
        // For non-survey pages, use the provided backgroundColor directly
        setEffectiveBackgroundColor(backgroundColor);
        if (backgroundColor !== "#FFFFFF") {
          setHasReceivedColor(true);
        }
      } else {
        setEffectiveBackgroundColor(backgroundColor);
      }
    };

    updateBackgroundColor();

    // Set up a simple interval to check for updates (faster for first load)
    if (coverage === "full") {
      const interval = setInterval(updateBackgroundColor, 50);
      return () => clearInterval(interval);
    }
  }, [backgroundColor, coverage]);

  // Update the background color dynamically when it changes
  useEffect(() => {
    if (coverage === "full" && portalContainer) {
      const loaderElement = portalContainer.querySelector('.loader-background');
      if (loaderElement) {
        loaderElement.style.backgroundColor = effectiveBackgroundColor;
      }
    }
  }, [effectiveBackgroundColor, portalContainer, coverage]);

  // Check if this is a survey page
  const isSurveyPage = typeof window !== 'undefined' && window.location.pathname.startsWith('/s/');

  // Adapt the loading-bar colors to the survey background so the bar never gets
  // swallowed by dark/colored backgrounds. We keep three clearly distinct
  // tones: background -> a faint rail -> a solid, fully opaque moving bar that
  // always contrasts strongly with the chosen background hue.
  const isLightBg = isLightColor(effectiveBackgroundColor);
  const trackColor = isLightBg ? "rgba(0, 0, 0, 0.10)" : "rgba(255, 255, 255, 0.18)";
  const barColor = isLightBg ? "#1F2937" : "#FFFFFF";

  const loaderElement = (
    <div className={`flex flex-col items-center justify-center ${coverage === "self" ? "" : "min-h-[200px]"} ${className || ""}`}>
      {isSurveyPage && coverage === "full" && (
        <div className="mb-3">
          <PollBoltLogo isLight={isLightBg} />
        </div>
      )}
      <div className="loading-bar" style={{ backgroundColor: trackColor }}>
        <div className="blue-bar" style={{ backgroundColor: barColor }}></div>
      </div>
    </div>
  );

  if (coverage === "full") {
    if (!portalContainer) return null;
    
    // For survey pages without a cached color, start with the background color but fade in smoothly
    const isSurveyPageWithoutCachedColor = isSurveyPage && !hasReceivedColor;
    
    return createPortal(
      <div 
        className="fixed inset-0 z-[9999] flex items-center justify-center loader-background" 
        style={{ 
          backgroundColor: effectiveBackgroundColor,
          transition: 'background-color 0.3s ease-in-out',
          // Start slightly opaque if we don't have a color yet, to avoid jarring white flash
          opacity: isSurveyPageWithoutCachedColor ? 0.95 : 1,
        }}
      >
        {loaderElement}
      </div>,
      portalContainer
    );
  }

  if (coverage === "parent") {
    return (
      <div className="absolute rounded-xl h-full w-full inset-0 z-[9999] flex items-center justify-center loader-background" style={{ backgroundColor: effectiveBackgroundColor }}>
        {loaderElement}
      </div>
    );
  }

  return loaderElement;
}
